import { AbstractBlockchainService, NativeTokens, SupportedTokens } from '@trinity/blockchain-common';
import { MUnity__factory as MUnityFactory, MUnityTrinityEuler__factory as MUnityTrinityEulerFactory, XUnity__factory as XUnityFactory, XUnityTrinityEuler__factory as XUnityTrinityEulerFactory, } from '@trinity/contracts';
import { Management__factory as ManagementFactory, Receipt__factory as ReceiptFactory, Unity__factory as UnityFactory, } from '@trinity/contracts-management';
import { ZeroAddress } from 'ethers';
import { ContractNames } from '../contracts';
import { ReceiptStatusNames } from '../receipt';
import { Roles, RolesMatch } from '../roles';
const ContractNameByDepositableToken = {
    [NativeTokens.Main]: ContractNames.depositReceipt,
    [SupportedTokens.UTY]: ContractNames.redemptionReceipt,
};
export class EvmManagementService extends AbstractBlockchainService {
    constructor(wallet, options) {
        super();
        this.wallet = wallet;
        this.options = options;
    }
    getContractInstances() {
        return Promise.all([
            this.getManagementContract(),
            this.getDepositReceiptContract(),
            this.getRedemptionReceiptContract(),
            this.getUnityTokenContract(),
        ]);
    }
    get signer() {
        return this.wallet.getSigner();
    }
    async getManagementContract() {
        const signer = await this.signer;
        return ManagementFactory.connect(this.options.contractAddresses.management, signer);
    }
    async getDepositReceiptContract() {
        const signer = await this.signer;
        return ReceiptFactory.connect(this.options.contractAddresses.depositReceipt, signer);
    }
    async getRedemptionReceiptContract() {
        const signer = await this.signer;
        return ReceiptFactory.connect(this.options.contractAddresses.redemptionReceipt, signer);
    }
    async getUnityTokenContract() {
        const signer = await this.signer;
        return UnityFactory.connect(this.options.contractAddresses.unity, signer);
    }
    async getXUnityContract() {
        const signer = await this.signer;
        return XUnityFactory.connect(this.options.contractAddresses.xUnity, signer);
    }
    async getXUnityTrinityContract() {
        const signer = await this.signer;
        return XUnityTrinityEulerFactory.connect(this.options.contractAddresses.xUnityTrinity, signer);
    }
    async getMUnityContract() {
        const signer = await this.signer;
        return MUnityFactory.connect(this.options.contractAddresses.mUnity, signer);
    }
    async getMUnityTrinityContract() {
        const signer = await this.signer;
        return MUnityTrinityEulerFactory.connect(this.options.contractAddresses.mUnityTrinity, signer);
    }
    async getContractByName(contract) {
        switch (contract) {
            case ContractNames.management:
                return (await this.getManagementContract());
            case ContractNames.depositReceipt:
                return (await this.getDepositReceiptContract());
            case ContractNames.redemptionReceipt:
                return (await this.getRedemptionReceiptContract());
            case ContractNames.unityToken:
                return (await this.getUnityTokenContract());
            case ContractNames.XUnity:
                return (await this.getXUnityContract());
            case ContractNames.XUnityTrinityPool:
                return (await this.getXUnityTrinityContract());
            case ContractNames.mUnity:
                return (await this.getMUnityContract());
            case ContractNames.MUnityTrinityPool:
                return (await this.getMUnityTrinityContract());
            default:
                throw new Error(`Contract ${contract} not found`);
        }
    }
    getReceiptStatus(statusFromContract, token) {
        const receiptStatusMapping = {
            [NativeTokens.Main]: {
                [0]: ReceiptStatusNames.Pending,
                [1]: ReceiptStatusNames.Withdrawn,
                [2]: ReceiptStatusNames.Reconciled,
            },
            [SupportedTokens.UTY]: {
                [0]: ReceiptStatusNames.Pending,
                [1]: ReceiptStatusNames.Reconciled,
            },
        };
        return receiptStatusMapping[token][statusFromContract.toString()];
    }
    async getManagementContractRole(role) {
        const ContractRolesMapping = {
            [Roles.Operator]: (contract) => contract.OPERATOR_ROLE(),
            [Roles.Admin]: (contract) => contract.ADMIN_ROLE(),
            [Roles.Whitelisted]: (contract) => contract.WHITELIST_ROLE(),
            [Roles.Beneficiary]: (contract) => contract.BENEFICIARY_ROLE(),
            [Roles.DefaultAdmin]: (contract) => contract.DEFAULT_ADMIN_ROLE(),
        };
        const contract = await this.getContractByName(ContractNames.management);
        return this.callContract(contract, (contract) => ContractRolesMapping[role](contract));
    }
    async deposit(amount, token) {
        const contract = await this.getContractByName(ContractNames.management);
        const managementContractAddress = await this.callContract(contract, (contract) => contract.getAddress());
        if (token === NativeTokens.Main) {
            await this.wallet.transfer(amount, managementContractAddress);
        }
        else {
            const unityTokenContract = await this.getContractByName(ContractNames.unityToken);
            await this.waitForTransactionConfirmation(unityTokenContract, (contract) => contract.transfer(managementContractAddress, amount));
        }
    }
    async withdrawNativeToken(amount, beneficiaryAddress) {
        const contract = await this.getContractByName(ContractNames.management);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.withdraw(beneficiaryAddress, amount));
    }
    async withdrawDeposits(receiptIds, beneficiaryAddress) {
        const contract = await this.getContractByName(ContractNames.management);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.withdrawDeposits(receiptIds, beneficiaryAddress));
    }
    async reconcileDeposits(receiptIds, exchangeRate, token) {
        const contract = await this.getContractByName(ContractNames.management);
        await this.waitForTransactionConfirmation(contract, (contract) => {
            if (token === NativeTokens.Main) {
                return contract.markDepositsReconciled(receiptIds, exchangeRate);
            }
            return contract.markRedemptionsReconciled(receiptIds, exchangeRate);
        });
    }
    async redeemReceipt(receiptId, token) {
        const contract = await this.getContractByName(ContractNameByDepositableToken[token]);
        const walletAddress = await this.wallet.getAddress();
        const managementContract = await this.getContractByName(ContractNames.management);
        const managementContractAddress = await this.callContract(managementContract, (contract) => contract.getAddress());
        await this.waitForTransactionConfirmation(contract, async (contract) => {
            return contract.transferFrom(walletAddress, managementContractAddress, receiptId);
        });
    }
    async getReceipts(token) {
        const contract = await this.getContractByName(ContractNames.management);
        const receiptContract = await this.getContractByName(ContractNameByDepositableToken[token]);
        const [ids, receipts] = await this.callContract(contract, (contract) => {
            if (token === NativeTokens.Main) {
                return contract.getAllDeposits();
            }
            return contract.getAllRedemptions();
        });
        const getNftOwner = (id) => this.callContract(receiptContract, (contract) => contract.ownerOf(id));
        const getReceiptStatus = (statusFromContract) => this.getReceiptStatus(statusFromContract, token);
        const enrichedReceipts = await Promise.all(ids.map(async (id, index) => {
            const receipt = receipts[index];
            const { amount, exchangeRate, timestamp } = receipt;
            const timestampDate = new Date(Number(timestamp) * 1000);
            return {
                amount,
                exchangeRate,
                status: getReceiptStatus(receipt.status),
                id,
                timestamp: timestampDate,
                walletAddress: await getNftOwner(id),
            };
        }));
        return enrichedReceipts.sort(({ timestamp: timestampA }, { timestamp: timestampB }) => timestampB.getTime() - timestampA.getTime());
    }
    async hasRole(address, roles, match) {
        const contractRoles = await Promise.all(roles.map((role) => this.getManagementContractRole(role)));
        const contract = await this.getContractByName(ContractNames.management);
        const hasRoles = await Promise.all(contractRoles.map((contractRole) => contract.hasRole(contractRole, address)));
        if (match === RolesMatch.OneOf) {
            return hasRoles.some((hasRole) => hasRole);
        }
        return hasRoles.every((hasRole) => hasRole);
    }
    async isManagementContractOf(contractName, managementContractAddress) {
        const contract = await this.getContractByName(contractName);
        const contractRole = await this.callContract(contract, (contract) => contract.MANAGEMENT_SMART_CONTRACT_ROLE());
        return this.callContract(contract, (contract) => contract.hasRole(contractRole, managementContractAddress));
    }
    async addWalletToRole(address, role) {
        const contractRole = await this.getManagementContractRole(role);
        const contract = await this.getContractByName(ContractNames.management);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.grantRole(contractRole, address));
    }
    async removeWalletFromRole(address, role) {
        const contractRole = await this.getManagementContractRole(role);
        const contract = await this.getContractByName(ContractNames.management);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.revokeRole(contractRole, address));
    }
    async getRoleMembers(role) {
        const contractRole = await this.getManagementContractRole(role);
        const contract = await this.getContractByName(ContractNames.management);
        const members = await this.callContract(contract, (contract) => contract.getRoleMembers(contractRole));
        // We have to do this because the contract returns a Proxy(Result),
        // which doesn't play well with react-query. It will not see it as
        // something different and will not refetch the data when the query key changes.
        return members.map((member) => member);
    }
    async getContractAddress(contractName) {
        const contract = await this.getContractByName(contractName);
        return this.callContract(contract, (contract) => contract.getAddress());
    }
    async getCurrentOwner(contractName) {
        const contract = await this.getContractByName(contractName);
        return this.callContract(contract, (contract) => contract.defaultAdmin());
    }
    async startOwnershipTransfer(newOwner, contractName) {
        const contract = await this.getContractByName(contractName);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.beginDefaultAdminTransfer(newOwner));
    }
    async acceptOwnershipTransfer(contractName) {
        const contract = await this.getContractByName(contractName);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.acceptDefaultAdminTransfer());
    }
    async getPendingOwnership(contractName) {
        const contract = await this.getContractByName(contractName);
        const { newAdmin, schedule } = await this.callContract(contract, (contract) => contract.pendingDefaultAdmin());
        const newAdminAddress = newAdmin && newAdmin !== ZeroAddress ? newAdmin : undefined;
        return { newAdminAddress, schedule: new Date(Number(schedule) * 1000) };
    }
    async rejectOwnershipTransfer(contractName) {
        const contract = await this.getContractByName(contractName);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.cancelDefaultAdminTransfer());
    }
    async getMinimumDepositAmount(token) {
        const contract = await this.getContractByName(ContractNames.management);
        if (token === NativeTokens.Main) {
            return this.callContract(contract, (contract) => contract.minimumDepositAmount());
        }
        return this.callContract(contract, (contract) => contract.minimumRedemptionAmount());
    }
    async changeMinimumDepositAmount(newAmount, token) {
        const contract = await this.getContractByName(ContractNames.management);
        if (token === NativeTokens.Main) {
            return this.waitForTransactionConfirmation(contract, (contract) => contract.setMinDepositAmount(newAmount));
        }
        return this.waitForTransactionConfirmation(contract, (contract) => contract.setMinRedemptionAmount(newAmount));
    }
    async pause(contractName) {
        const contract = await this.getContractByName(contractName);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.pause());
    }
    async unpause(contractName) {
        const contract = await this.getContractByName(contractName);
        await this.waitForTransactionConfirmation(contract, (contract) => contract.unpause());
    }
    async isPaused(contractName) {
        const contract = await this.getContractByName(contractName);
        return this.callContract(contract, (contract) => contract.paused());
    }
    async getManagementContractNativeTokenBalance() {
        const contractAddress = await this.getContractAddress(ContractNames.management);
        const balance = await this.wallet.getProvider().getBalance(contractAddress, 'latest');
        return balance;
    }
    async getAddressesForRole(role) {
        const contract = await this.getContractByName(ContractNames.management);
        const contractRole = await this.getManagementContractRole(role);
        return this.callContract(contract, (contract) => contract.getRoleMembers(contractRole));
    }
}
//# sourceMappingURL=evm-management-service.js.map